import { Fragment, useMemo } from "react";
import { GrDivider, GrStack } from "@jnpmedi/grebob";
import qs from "qs";
import { Container } from "@newLayouts/common/Container";
import { PageNotFoundLayout } from "@newLayouts/common/PageNotFoundLayout";
import { ArchiverBrowseFolderTable } from "@newComponents/archiver/ArchiverBrowseFolderTable/ArchiverBrowseFolderTable";
import { ArchiverUsageBar } from "@newComponents/archiver/ArchiverUsageBar";
import { AddArchiverDocumentDialog } from "@newComponents/archiver/dialogs/AddArchiverDocumentDialog";
import { AddFolderDialog } from "@newComponents/common/dialogs/AddFolderDialog";
import { AuditTrailDialog } from "@newComponents/common/dialogs/AuditTrailDialog";
import { MoveResourceDialog } from "@newComponents/common/dialogs/MoveResourceDialog";
import { RemoveFileDialog } from "@newComponents/common/dialogs/RemoveFileDialog";
import { RemoveFolderDialog } from "@newComponents/common/dialogs/RemoveFolderDialog";
import { RenameResourceDialog } from "@newComponents/common/dialogs/RenameResourceDialog";
import { SharingSettingsDialog } from "@newComponents/common/dialogs/SharingSettingsDialog";
import { DropdownButtonIcon } from "@newComponents/common/DropdownButtonIcon";
import { DropSection } from "@newComponents/common/dropSection/DropSection";
import { IconTooltipButton } from "@newComponents/common/IconTooltipButton";
import { OrganizationDropdown } from "@newComponents/common/OrganizationDropdown";
import { ShowDeletedItemCheckbox } from "@newComponents/common/ShowDeletedItemCheckbox";
import { UploadDropdownButton } from "@newComponents/common/UploadDropdownButton";
import { ViewportCenterCircle } from "@newComponents/common/ViewportCenterCircle";
import { MvnMessage } from "@newLibs/utils";
import { useBoolean } from "@newHooks/common/useBoolean";
import { useFolderContext } from "@newHooks/context/folder/folder.context";
import { useOrganizationContext } from "@newHooks/context/organization/organization.context";
import { useFolderByIdQuery } from "@newHooks/query/folder/folderById.query";
import { useFoldersSummaryQuery } from "@newHooks/query/folder/foldersSummary.query";
import { useInventoryBalanceQuery } from "@newHooks/query/inventory/inventoryBalance.query";
import { useDocsPdfServiceInspectQuery } from "@newHooks/query/serviceInspect/docsPdfServiceInspect.query";
import { INCLUDE_DELETED_RESOURCE_QUERY_KEY, useIncludeDeletedQueryParam } from "@newHooks/route/useIncludeDeletedQueryParam";
import { useRoute } from "@newHooks/route/useRoute";
import { useArchiverAddDropdown } from "./hooks/useArchiverAddDropdown";
import { useArchiverDialogs } from "./hooks/useArchiverDialogs";
import { useArchiverHandlers } from "./hooks/useArchiverHandlers";
import { COMMON_LANG } from "@maven-msg/lang/common";
import { BundleItemFunctionalityType } from "@maven-rest/billing/Bundle.dto";
import { isSubscribedFunctionality } from "@maven-rest/billing/SubscriptionLicense.lib";
import { FolderType } from "@maven-rest/docs/Folder.dto";
import { ellipsis, findBigintSafe } from "@maven-helper/functions/Misc.function";
import { JsonHelper } from "@maven-helper/helpers/Json.helper";

export function ArchiverBrowseFolderPage() {
  const { query } = useRoute();
  const { folderIds, currentFolderId } = useFolderContext();
  const dialogs = useArchiverDialogs();
  const { organization, organizations, isLoading: isOrganizationLoading } = useOrganizationContext();

  const isDeletedResource = useBoolean(!!JsonHelper.tryParse<boolean>(String(query[INCLUDE_DELETED_RESOURCE_QUERY_KEY])));

  useDocsPdfServiceInspectQuery();

  const { data: folderByIdData, isInitialLoading: isFolderByIdInitialLoading } = useFolderByIdQuery(
    { folderId: currentFolderId ?? BigInt(-1) },
    { enabled: !!currentFolderId }
  );

  const { data: foldersSummaryData } = useFoldersSummaryQuery(
    {
      organizationId: folderByIdData?.folder.organizationId ?? BigInt(-1),
      folderIds: folderIds.length > 0 ? folderIds.join(",") : "",
      folderType: FolderType.ARCHIVER,
      includeDeletedResource: isDeletedResource.value,
    },
    { enabled: !!folderByIdData?.folder.organizationId }
  );

  const { data: inventoryBalanceData } = useInventoryBalanceQuery(
    {
      organizationId: organization?.id || BigInt(-1),
      functionality: BundleItemFunctionalityType.Docs_Archiver_Storage,
    },
    { enabled: !!organization }
  );

  const folderId = folderByIdData?.folder.id;
  const isDeletedFolder = !!folderByIdData?.folder.isDestroyed;

  const isDocsArchiverViewerSubscribe = useMemo(() => {
    return !!organization && isSubscribedFunctionality(organization.licenses, BundleItemFunctionalityType.Docs_Archiver_Viewer);
  }, [organization]);

  const dropdownMenuList = useArchiverAddDropdown({ folderCount: folderIds.length, isDocsArchiverViewerSubscribe, ...dialogs });
  const { clickedRow, clickHandler } = useArchiverHandlers(dialogs);

  useIncludeDeletedQueryParam({
    key: INCLUDE_DELETED_RESOURCE_QUERY_KEY,
    value: isDeletedResource.value,
  });

  if (isOrganizationLoading) {
    return <ViewportCenterCircle />;
  }

  if (!organization) {
    return <PageNotFoundLayout />;
  }

  return (
    <Fragment key={String(organization.id)}>
      <Container
        title={{
          text: folderByIdData?.folder.name || "Archiver",
          textLoading: isFolderByIdInitialLoading,
          components: !folderByIdData?.folder && (
            <OrganizationDropdown selectedOrganizationId={organization.id} organizations={organizations} />
          ),
        }}
        breadcrumbs={[
          { label: "Archiver", href: `/archiver/folders${Object.keys(query).length ? `?${qs.stringify(query)}` : ""}` },
          ...folderIds.map((folderId, folderIdx) => ({
            label: ellipsis((!!foldersSummaryData?.folders && findBigintSafe(foldersSummaryData.folders, "id", folderId)?.name) || "", 20),
            href:
              folderIdx + 1 === folderIds.length
                ? undefined
                : ["/archiver/folders"]
                    .concat(folderIds.slice(0, folderIdx + 1).map((e) => String(e)))
                    .join("/")
                    .concat(Object.keys(query).length ? `?${qs.stringify(query)}` : ""),
          })),
        ]}
      >
        <GrStack flexDirection="column" height="inherit">
          <GrStack justifyContent="space-between" style={{ marginBottom: "8px" }}>
            <ArchiverUsageBar
              organizationId={organization.id}
              isDeletedResource={isDeletedResource.value}
              storageUsage={{
                total: inventoryBalanceData?.inventoryBalance.quantity ?? 0,
                usage: inventoryBalanceData?.inventoryBalance.used ?? 0,
              }}
            />

            {isDocsArchiverViewerSubscribe && (
              <GrStack>
                <GrStack gap="4px">
                  <IconTooltipButton
                    iconType="report-search"
                    tooltipContent={MvnMessage(COMMON_LANG.AUDIT_TRAIL)}
                    onClick={dialogs.openAuditTrailDialog.onTrue}
                  />
                  <DropdownButtonIcon
                    menuOptions={{
                      items: [
                        {
                          type: "item",
                          children: <ShowDeletedItemCheckbox isDeletedResource={isDeletedResource} />,
                          disabled: isDeletedFolder,
                        },
                      ],
                    }}
                  />
                </GrStack>

                <GrDivider orientation="vertical" spacing={8} />

                <UploadDropdownButton
                  menuOptions={{
                    items: dropdownMenuList,
                    size: "medium",
                    position: { horizontal: "right", vertical: "bottom" },
                  }}
                  disabled={isDeletedFolder}
                />
              </GrStack>
            )}
          </GrStack>

          <DropSection folderId={folderId} folderType={FolderType.ARCHIVER}>
            <ArchiverBrowseFolderTable
              organizationId={organization.id}
              isDeletedResource={isDeletedResource.value}
              onSharingSettingsClick={clickHandler.handleSharingSettingsClick}
              onMoveToClick={clickHandler.handleMoveToClick}
              onRenameClick={clickHandler.handleRenameClick}
              onDeleteFolderClick={clickHandler.handleDeleteFolderClick}
              onDeleteFileClick={clickHandler.handleDeleteFileClick}
            />
          </DropSection>
        </GrStack>
      </Container>

      {dialogs.openCreateFolderDialog.value && (
        <AddFolderDialog
          open={dialogs.openCreateFolderDialog}
          folderId={folderId}
          organization={organization}
          folderType={FolderType.ARCHIVER}
        />
      )}
      {dialogs.openCreateDocumentDialog.value && (
        <AddArchiverDocumentDialog
          open={dialogs.openCreateDocumentDialog}
          folderId={folderId}
          organization={organization}
          storageUsage={{
            total: inventoryBalanceData?.inventoryBalance.quantity ?? 0,
            usage: inventoryBalanceData?.inventoryBalance.used ?? 0,
          }}
        />
      )}
      {dialogs.openAuditTrailDialog.value && (
        <AuditTrailDialog
          open={dialogs.openAuditTrailDialog}
          organizationId={organization.id}
          folderType={FolderType.ARCHIVER}
          folderId={folderId}
          includeDeletedResource={isDeletedResource.value}
        />
      )}
      {!!clickedRow.sharingTarget && dialogs.openSharingDialog.value && (
        <SharingSettingsDialog
          open={dialogs.openSharingDialog}
          organizationId={organization.id}
          sharingTarget={clickedRow.sharingTarget}
          folderType={FolderType.ARCHIVER}
        />
      )}
      {!!clickedRow.moveResourceTarget && dialogs.openMoveResourceDialog.value && (
        <MoveResourceDialog
          open={dialogs.openMoveResourceDialog}
          organization={organization}
          baseFolderType={FolderType.ARCHIVER}
          targetResource={clickedRow.moveResourceTarget}
        />
      )}
      {!!clickedRow.renameResourceTarget && dialogs.openRenameResourceDialog.value && (
        <RenameResourceDialog open={dialogs.openRenameResourceDialog} renameResource={clickedRow.renameResourceTarget} />
      )}
      {!!clickedRow.removeFolderTarget && dialogs.openRemoveFolderDialog.value && (
        <RemoveFolderDialog {...clickedRow.removeFolderTarget} open={dialogs.openRemoveFolderDialog} />
      )}
      {!!clickedRow.removeFileTarget && dialogs.openRemoveFileDialog.value && (
        <RemoveFileDialog {...clickedRow.removeFileTarget} open={dialogs.openRemoveFileDialog} />
      )}
    </Fragment>
  );
}
